import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { SearchX } from 'lucide-react'
import { useFilterLabels } from './useFilterLabels'
import type { FilterContext } from '@/lib/filters/applyFilters'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import type { FilterFieldKey } from '@/lib/types/filter'
import { Button } from '@/components/ui/button'
import { findStrictestFilter } from '@/lib/filters/findStrictestFilter'
import { EMPTY_FILTERS, getActiveFilterCount } from '@/lib/types/filter'
import useProjectFilterStore from '@/store/ProjectFilterStore'

interface FilterZeroResultsProps {
  projectId: string
  /** Tasks in scope before filters were applied. */
  unfilteredTasks: ReadonlyArray<TaskDocument>
  filterContext?: FilterContext
  /** Filters that can't be removed here (e.g. project on a space). */
  pinnedFields?: Array<FilterFieldKey>
}

/**
 * Zero results: say which filter is too strict, and offer the fix (design 9c).
 */
export function FilterZeroResults({
  projectId,
  unfilteredTasks,
  filterContext,
  pinnedFields,
}: FilterZeroResultsProps) {
  const { t } = useTranslation('tasks')
  const filters = useProjectFilterStore((s) => s.filters[projectId] ?? EMPTY_FILTERS)
  const removeFilter = useProjectFilterStore((s) => s.removeFilter)
  const clearFilters = useProjectFilterStore((s) => s.clearFilters)
  const setValues = useProjectFilterStore((s) => s.setValues)

  const removable = useMemo(() => {
    const pinned = new Set(pinnedFields ?? [])
    return filters.filter((f) => !pinned.has(f.fieldKey))
  }, [filters, pinnedFields])

  const strictest = useMemo(() => {
    const pinned = new Set(pinnedFields ?? [])
    const result = findStrictestFilter(unfilteredTasks, filters, filterContext)
    return result && !pinned.has(result.filter.fieldKey) ? result : null
  }, [unfilteredTasks, filters, filterContext, pinnedFields])

  const { fieldLabel, describe } = useFilterLabels()
  // What's applied, as the chips read, and how much it hides (design 33b).
  const summary = filters
    .filter((f) => f.values.length > 0)
    .map(describe)
    .join(' · ')
  const label = (key: FilterFieldKey) => fieldLabel(key)

  const clearAll = () => {
    const pinned = new Set(pinnedFields ?? [])
    if (pinned.size === 0) {
      clearFilters(projectId)
      return
    }
    for (const f of filters) {
      if (!f.id) continue
      // Pinned filters stay but lose their values.
      if (pinned.has(f.fieldKey)) setValues(projectId, f.id, [])
      else removeFilter(projectId, f.id)
    }
  }

  return (
    <div className="mx-auto mt-16 flex max-w-sm flex-col items-center px-6 text-center">
      <div className="mb-3 flex size-10 items-center justify-center rounded-full bg-secondary">
        <SearchX className="size-5 text-muted-foreground" />
      </div>
      <p className="text-[15px] font-semibold">
        {t('filters.zero.title', { count: getActiveFilterCount(filters) })}
      </p>
      {(summary || unfilteredTasks.length > 0) && (
        <p className="mt-1 text-[13px] text-muted-foreground">
          {summary && `${summary}. `}
          {unfilteredTasks.length > 0 && t('filters.zero.hidden', { count: unfilteredTasks.length })}
        </p>
      )}
      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        {strictest && (
          <Button size="sm" onClick={() => strictest.filter.id && removeFilter(projectId, strictest.filter.id)}>
            {t('filters.zero.remove', {
              field: label(strictest.filter.fieldKey),
              count: strictest.countWithout,
            })}
          </Button>
        )}
        {removable.length > 0 && (
          <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={clearAll}>
            {t('filters.zero.clearAll')}
          </Button>
        )}
      </div>
    </div>
  )
}
